/**
 * Reload-and-reopen for the recorder after a camera refusal.
 *
 * Some browsers keep refusing getUserMedia for the rest of the page's life
 * once the person has said no, even after they allow camera and microphone
 * (Safari answers a second request with NotAllowedError without asking;
 * Chrome can need a reload after a site-settings change). "Try again" then
 * did nothing. A reload clears that refusal, so the recorder reloads the page
 * once and the flow opens straight back into the recorder.
 *
 * sessionStorage: survives the reload, scoped to the tab.
 */

const REOPEN_KEY = 'sv_recorder_reopen';
const RELOADED_AT_KEY = 'sv_recorder_reloaded_at';
/** One reload per this window: a refusal that survives it is not page-level. */
const RELOAD_WINDOW_MS = 2 * 60 * 1000;

const read = (key: string): string | null => {
  try {
    return typeof window === 'undefined' ? null : window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

/** False when the page was already reloaded for the recorder a moment ago. */
export const canReloadForRecorder = (): boolean => {
  const at = Number(read(RELOADED_AT_KEY));
  return !at || Date.now() - at > RELOAD_WINDOW_MS;
};

export const reloadIntoRecorder = (): void => {
  try {
    window.sessionStorage.setItem(REOPEN_KEY, '1');
    window.sessionStorage.setItem(RELOADED_AT_KEY, String(Date.now()));
  } catch {
    return; // without storage the reload would land on the ticket: not worth it
  }
  window.location.reload();
};

/** Set by a reload in the last RELOAD_WINDOW_MS; an older flag is stale. */
export const shouldReopenRecorder = (): boolean => read(REOPEN_KEY) === '1' && !canReloadForRecorder();

export const clearRecorderReopen = (): void => {
  try {
    window.sessionStorage.removeItem(REOPEN_KEY);
  } catch {
    /* ignore */
  }
};
